import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, FileText, ArrowRight, CheckCircle2, Download, Briefcase, FlaskConical, Wheat, Sprout, Tractor, ShieldCheck, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const sections = [{
  title: 'Enterprise Guides',
  icon: Briefcase,
  color: 'bg-amber-600',
  description: 'Practical guides for establishing and managing agricultural enterprises across various value chains in Uganda.',
  items: [{
    name: 'Poultry Enterprise Guide',
    desc: 'Step-by-step guide for starting and managing a profitable poultry (layers and broilers) enterprise.'
  }, {
    name: 'Dairy Farming Enterprise Guide',
    desc: 'Best practices for dairy cattle management, feeding, breeding, and milk marketing.'
  }, {
    name: 'Apiculture (Beekeeping) Enterprise Guide',
    desc: 'Guide to modern beekeeping, honey harvesting, processing, and marketing.'
  }, {
    name: 'Piggery Enterprise Guide',
    desc: 'Comprehensive guide on pig production, housing, feeding, and disease management.'
  }, {
    name: 'Fish Farming Enterprise Guide',
    desc: 'Aquaculture enterprise setup including pond construction, stocking, feeding, and harvesting.'
  }, {
    name: 'Coffee Production Enterprise Guide',
    desc: 'Robusta and Arabica coffee production, post-harvest handling, and quality standards.'
  }, {
    name: 'Horticulture Enterprise Guide',
    desc: 'Commercial production of fruits, vegetables, and flowers for domestic and export markets.'
  }, {
    name: 'Maize Production Enterprise Guide',
    desc: 'Improved maize agronomy, post-harvest handling, and marketing strategies.'
  }]
}, {
  title: 'Chemical, Premises and Fumigant Registers',
  icon: FlaskConical,
  color: 'bg-red-600',
  description: 'Official registers of approved agricultural chemicals, licensed premises, and authorized fumigant operators in Uganda.',
  items: [{
    name: 'Register of Approved Agricultural Chemicals',
    desc: 'Complete list of pesticides, herbicides, and fungicides registered for use in Uganda by the Agricultural Chemicals Board.'
  }, {
    name: 'Register of Licensed Agrochemical Premises',
    desc: 'Directory of licensed agrochemical dealers, distributors, and storage facilities across Uganda.'
  }, {
    name: 'Register of Authorized Fumigant Operators',
    desc: 'List of certified fumigation service providers approved to conduct fumigation of agricultural commodities.'
  }, {
    name: 'Banned and Restricted Chemicals List',
    desc: 'Chemicals prohibited or restricted from use in Uganda in compliance with international conventions.'
  }, {
    name: 'Agrochemical Import Guidelines',
    desc: 'Procedures and requirements for importing agricultural chemicals into Uganda.'
  }, {
    name: 'Pesticide Residue Limits (MRLs)',
    desc: 'Maximum Residue Limits for pesticides on agricultural produce for domestic consumption and export.'
  }]
}, {
  title: 'Crop Sub-sector',
  icon: Wheat,
  color: 'bg-green-600',
  description: 'Guidelines, standards, and technical documents for crop production, protection, inspection, and certification.',
  items: [{
    name: 'Seed Certification Standards and Procedures',
    desc: 'National standards for seed production, processing, testing, and certification under the Seeds and Plant Act.'
  }, {
    name: 'Phytosanitary Inspection Guidelines',
    desc: 'Procedures for plant health inspection of agricultural commodities for import and export.'
  }, {
    name: 'Crop Variety Release Procedures',
    desc: 'Process for evaluation, testing, and official release of new crop varieties in Uganda.'
  }, {
    name: 'Plant Quarantine Regulations',
    desc: 'Regulations governing the importation and movement of plant materials to prevent pest introduction.'
  }, {
    name: 'Good Agricultural Practices (GAP) for Crops',
    desc: 'Standards for sustainable crop production, food safety, and environmental protection.'
  }, {
    name: 'Post-Harvest Handling Guidelines for Grains',
    desc: 'Best practices for drying, storage, and preservation of cereal grains and pulses.'
  }, {
    name: 'Organic Crop Production Standards',
    desc: 'National standards for organic crop production, processing, and certification.'
  }, {
    name: 'Integrated Pest Management (IPM) Guidelines',
    desc: 'Strategies for managing crop pests using biological, cultural, and chemical methods.'
  }]
}, {
  title: 'MAAIF Agricultural Extension Services',
  icon: Sprout,
  color: 'bg-emerald-600',
  description: 'Extension service delivery guidelines, farmer engagement approaches, and advisory service frameworks.',
  items: [{
    name: 'National Agricultural Extension Policy',
    desc: 'Policy framework for delivery of pluralistic, demand-driven agricultural extension services.'
  }, {
    name: 'Farmer Field School (FFS) Methodology Guide',
    desc: 'Step-by-step guide for establishing and facilitating Farmer Field Schools for participatory learning.'
  }, {
    name: 'Agricultural Extension Worker Handbook',
    desc: 'Reference manual for district and sub-county agricultural extension officers.'
  }, {
    name: 'Community-Based Facilitator Training Manual',
    desc: 'Training curriculum for community agricultural facilitators and lead farmers.'
  }, {
    name: 'ICT in Agricultural Extension Guidelines',
    desc: 'Framework for using mobile phones, radio, and digital platforms for extension service delivery.'
  }, {
    name: 'Farmer Group Formation and Management Guide',
    desc: 'Guidelines for organizing, registering, and strengthening farmer groups and cooperatives.'
  }, {
    name: 'Agricultural Advisory Services Standards',
    desc: 'Quality standards and performance benchmarks for public and private extension service providers.'
  }]
}, {
  title: 'Agricultural Mechanisation Services',
  icon: Tractor,
  color: 'bg-blue-600',
  description: 'Guidelines for agricultural mechanization, equipment standards, and tractor hire services.',
  items: [{
    name: 'National Agricultural Mechanisation Policy',
    desc: 'Strategic framework for promoting appropriate mechanization along the agricultural value chain.'
  }, {
    name: 'Tractor Hire Services Guidelines',
    desc: 'Operational guidelines for government and private tractor hire schemes for smallholder farmers.'
  }, {
    name: 'Farm Machinery and Equipment Standards',
    desc: 'Technical standards and specifications for agricultural machinery imported and used in Uganda.'
  }, {
    name: 'Post-Harvest Processing Equipment Guide',
    desc: 'Guide to appropriate post-harvest processing machinery for grains, oilseeds, and root crops.'
  }, {
    name: 'Irrigation Equipment and Installation Standards',
    desc: 'Technical specifications for drip, sprinkler, and gravity-fed irrigation systems.'
  }, {
    name: 'Agricultural Mechanisation Training Curriculum',
    desc: 'Training modules for tractor operators, mechanics, and agricultural machinery technicians.'
  }]
}, {
  title: 'Animal Sub-sector',
  icon: ShieldCheck,
  color: 'bg-purple-600',
  description: 'Guidelines for animal health, livestock production, veterinary services, and animal product quality assurance.',
  items: [{
    name: 'Livestock Disease Control Guidelines',
    desc: 'Protocols for prevention, surveillance, and control of notifiable animal diseases in Uganda.'
  }, {
    name: 'Veterinary Drug Use and Regulation Guidelines',
    desc: 'Standards for registration, distribution, and use of veterinary drugs and biologicals.'
  }, {
    name: 'Livestock Movement Control Regulations',
    desc: 'Rules governing the movement of livestock to prevent spread of transboundary animal diseases.'
  }, {
    name: 'Meat Inspection and Hygiene Standards',
    desc: 'Standards for ante-mortem and post-mortem inspection of animals and meat hygiene in abattoirs.'
  }, {
    name: 'Dairy Production and Quality Standards',
    desc: 'Guidelines for hygienic milk production, collection, processing, and marketing.'
  }, {
    name: 'Animal Breeding and Genetics Guidelines',
    desc: 'Standards for artificial insemination services, breed improvement, and genetic resource conservation.'
  }, {
    name: 'Hides and Skins Quality Improvement Guide',
    desc: 'Best practices for flaying, preservation, and grading of hides and skins.'
  }, {
    name: 'Poultry Health Management Manual',
    desc: 'Disease prevention, biosecurity, and vaccination protocols for commercial and backyard poultry.'
  }]
}, {
  title: 'Training Manuals & Protocols',
  icon: BookOpen,
  color: 'bg-sky-600',
  description: 'Training materials, standard operating procedures, and technical protocols for agricultural service delivery.',
  items: [{
    name: 'Soil and Water Conservation Training Manual',
    desc: 'Practical training guide on terracing, contour farming, mulching, and water harvesting techniques.'
  }, {
    name: 'Climate Smart Agriculture Training Manual',
    desc: 'Training modules on climate-resilient farming practices, adaptation, and mitigation strategies.'
  }, {
    name: 'Food Safety and Quality Assurance Protocol',
    desc: 'Standard operating procedures for food safety testing, inspection, and certification.'
  }, {
    name: 'Laboratory Standard Operating Procedures',
    desc: 'SOPs for soil testing, plant pathology diagnostics, and veterinary laboratory analysis.'
  }, {
    name: 'Pest Surveillance and Reporting Protocol',
    desc: 'Procedures for field-level pest and disease surveillance, data collection, and reporting.'
  }, {
    name: 'Emergency Response Protocol for Disease Outbreaks',
    desc: 'Rapid response procedures for animal and plant disease outbreaks and natural disasters.'
  }, {
    name: 'Gender Mainstreaming in Agriculture Manual',
    desc: 'Guidelines for integrating gender considerations in agricultural programs and extension services.'
  }, {
    name: 'Youth in Agriculture Training Toolkit',
    desc: 'Training resources for engaging youth in modern, profitable agricultural enterprises.'
  }]
}];
function SectionCard({
  section,
  index
}: {
  section: (typeof sections)[0];
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const Icon = section.icon;
  const visibleItems = expanded ? section.items : section.items.slice(0, 4);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay: index * 0.08
  }} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-800">
      <div className="p-6 md:p-8" data-id="element-801">
        <div className="flex items-center gap-4 mb-4" data-id="element-802">
          <div className={`w-14 h-14 rounded-xl ${section.color} flex items-center justify-center flex-shrink-0`} data-id="element-803">
            <Icon className="w-7 h-7 text-white" data-id="element-804" />
          </div>
          <div data-id="element-805">
            <h2 className="text-2xl font-bold text-uganda-black" data-id="element-806">
              {section.title}
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-807">{section.description}</p>
          </div>
        </div>

        <div className="mt-6 space-y-3" data-id="element-808">
          {visibleItems.map((item, i) => <div key={i} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors group" data-id="element-809">
              <div className="flex items-start gap-3 flex-1" data-id="element-810">
                <FileText className="w-5 h-5 text-uganda-green flex-shrink-0 mt-0.5" data-id="element-811" />
                <div data-id="element-812">
                  <h3 className="font-semibold text-uganda-black text-sm" data-id="element-813">
                    {item.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-814">{item.desc}</p>
                </div>
              </div>
              <button className="flex items-center gap-1 text-sm font-semibold text-uganda-green hover:text-green-800 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-815">
                <Download className="w-4 h-4" data-id="element-816" /> Download
              </button>
            </div>)}
        </div>

        {section.items.length > 4 && <button onClick={() => setExpanded(!expanded)} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-uganda-green hover:text-green-800 transition-colors" data-id="element-817">
            {expanded ? 'Show Less' : `View All ${section.items.length} Documents`}
            {expanded ? <ChevronUp className="w-4 h-4" data-id="element-818" /> : <ChevronDown className="w-4 h-4" data-id="element-819" />}
          </button>}
      </div>
    </motion.div>;
}
export function GuidelinesServicesPage() {
  return <>
      <Header data-id="element-820" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-821">
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-822">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?w=1600&h=600&fit=crop")'
        }} data-id="element-823"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-824"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-825">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-826">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-827">
                <Home className="w-4 h-4 mr-1" data-id="element-828" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-829" />
              <span data-id="element-830">Services & Applications</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-831" />
              <span className="text-white font-medium" data-id="element-832">
                Guidelines & Services Center
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-833">
              Guidelines & Services Center
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-834">
              Access enterprise guides, regulatory registers, technical
              guidelines, training manuals, and standard operating procedures
              from the Ministry of Agriculture, Animal Industry and Fisheries.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-835">
          {/* Quick Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-10" data-id="element-836">
            <h3 className="font-bold text-uganda-black mb-4" data-id="element-837">
              Quick Navigation
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-838">
              {sections.map(section => <a key={section.title} href={`#${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="px-4 py-2 rounded-lg text-sm font-medium bg-uganda-light-green text-uganda-green hover:bg-green-100 transition-colors border border-green-100" data-id="element-839">
                  {section.title}
                </a>)}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-8" data-id="element-840">
            {sections.map((section, index) => <div key={section.title} id={section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')} data-id="element-841">
                <SectionCard section={section} index={index} data-id="element-842" />
              </div>)}
          </div>
        </div>
      </main>
      <Footer data-id="element-843" />
    </>;
}